import React, { useState, useEffect } from "react";
import { Check, Loader2, Cloud, LogOut, FilePlus2, FolderOpen, HelpCircle, ChevronDown, CreditCard, Pencil, Trash2, CopyPlus, ArrowLeft, User as UserIcon, CalendarDays, BookOpen, Settings, ChefHat, Home as HomeIcon, ShieldAlert, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STEPS } from "@/lib/planModel";
import { useAuth } from "@/context/AuthContext";
import { athleteTitle } from "@/lib/quirks";
import api from "@/lib/api";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import ThemeToggle from "@/components/ThemeToggle";
import HelpDialog from "@/components/HelpDialog";
import SubscriptionDialog from "@/components/SubscriptionDialog";
import AccountSettings from "@/components/AccountSettings";
import PrimaryNav from "@/components/PrimaryNav";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header({
  currentStep, maxStep = 1, onStepClick, saving, dirty,
  onNewPlan, plans = [], activePlanId, onLoadPlan, onRefreshPlans,
  onDuplicate, onRenamePlan, onDeletePlan, steps, coachEditing = "",
}) {
  const { email, logout, isAdmin, hasTracker, planTier } = useAuth();
  // Auto-earned athlete title (Cog / Machine / Nutrition Assassin) from the
  // current streak. Fetched lazily; hidden gracefully when there's no data.
  const [title, setTitle] = useState(null);
  useEffect(() => {
    if (!email) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/wellbeing/recap?days=90");
        if (!alive) return;
        // Compute streak by walking the returned days from the tail back,
        // counting consecutive active days. Using the data's own dates avoids
        // any browser-clock timezone drift. A day counts as "active" only
        // when it has real data (beta bug Feb 26 2026: brand new users
        // saw "Machine · 30+ day streak" from day one).
        const days = data?.days || [];
        const isLogged = (d) => {
          if (!d) return false;
          return Number(d.kcal) > 0
            || !!d.notes
            || Number(d.completedSessions) > 0;
        };
        let streak = 0;
        // Give athletes a grace window for today — if today has no data
        // yet, start the walk from yesterday so a morning visitor still
        // sees their current streak.
        const lastIdx = days.length - 1;
        const startIdx = lastIdx >= 0 && !isLogged(days[lastIdx]) ? lastIdx - 1 : lastIdx;
        for (let i = startIdx; i >= 0; i -= 1) {
          if (!isLogged(days[i])) break;
          streak += 1;
        }
        const sessions = days.reduce((n, d) => n + (d.completedSessions || 0), 0);
        setTitle(athleteTitle({ streak, sessions }));
      } catch { /* silent — this is decoration */ }
    })();
    return () => { alive = false; };
  }, [email]);
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Impersonation banner — set by AdminDashboard.impersonate() before the
  // page reload. When present, the admin sees a persistent yellow bar
  // across the top identifying whose account they're inside and an Exit
  // button that restores the stashed admin token in one tap.
  const impersonating = (() => {
    try {
      const raw = localStorage.getItem("spc_impersonating");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const exitImpersonation = () => {
    const admin = localStorage.getItem("spc_admin_token");
    if (admin) localStorage.setItem("spc_token", admin);
    localStorage.removeItem("spc_admin_token");
    localStorage.removeItem("spc_impersonating");
    window.location.href = "/admin";
  };

  const handleRename = (p) => {
    const n = window.prompt("Rename plan", p.name || "");
    if (n && n.trim() && n.trim() !== p.name) onRenamePlan(p.id, n.trim());
  };
  const handleDelete = (p) => {
    if (window.confirm(`Delete "${p.name || "this plan"}"? This cannot be undone.`)) onDeletePlan(p.id);
  };

  // Auto-open the guide once per user (first login on this device).
  useEffect(() => {
    const key = `spc_help_seen_${email || "guest"}`;
    if (!localStorage.getItem(key)) {
      setHelpOpen(true);
      localStorage.setItem(key, "1");
    }
  }, [email]);

  return (
    <>
      {impersonating && (
        <div data-testid="impersonation-banner"
          className="sticky top-0 z-50 bg-[#E2E30A] text-black flex items-center gap-3 px-4 py-2 shadow-lg no-print">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <p className="flex-1 text-[11px] sm:text-xs uppercase tracking-wider font-bold leading-tight">
            Viewing as <span className="font-mono normal-case">{impersonating.email}</span>
            {impersonating.name ? <span className="ml-1 opacity-70">· {impersonating.name}</span> : null}
          </p>
          <button data-testid="impersonation-exit" onClick={exitImpersonation}
            className="bg-black text-[#E2E30A] px-3 py-1 text-[10px] uppercase tracking-wider font-bold hover:bg-black/80 transition-colors">
            Exit
          </button>
        </div>
      )}
    <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10 no-print">
      {/* Row 1 — brand lockup + utilities */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
        <BrandLockup />

        <div className="flex items-center gap-2 sm:gap-3 ml-auto shrink-0">
          <div className="flex items-center gap-1.5 text-xs" data-testid="autosave-indicator">
            {saving ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin text-[#4C9AFF]" /><span className="text-zinc-200 hidden md:inline">Saving…</span></>
            ) : dirty ? (
              <><Cloud className="h-3.5 w-3.5 text-[#E2E30A]" /><span className="text-zinc-200 hidden md:inline">Unsaved</span></>
            ) : (
              <><Check className="h-3.5 w-3.5 text-[#E2E30A]" /><span className="text-zinc-200 hidden md:inline">Saved</span></>
            )}
          </div>
          <HeaderUtils />
        </div>
      </div>

      {/* Row 2 — shared PrimaryNav (Plan · Tracker · Race · Recipes · Guide ·
          Insights) with the Plans dropdown docked on the right. */}
      <PrimaryNav plans={plans} right={coachEditing ? (
          <span className="h-8 px-3 inline-flex items-center bg-[#34C759]/15 border border-[#34C759]/50 text-[#34C759] text-[10px] font-bold uppercase tracking-wider"
            data-testid="coach-editing-chip">
            Coach mode
          </span>
        ) : (
          <DropdownMenu onOpenChange={(o) => o && onRefreshPlans && onRefreshPlans()}>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="action-plans"
                className="h-8 px-3 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-[11px] font-bold uppercase tracking-wider transition-colors"
              >
                <FolderOpen className="h-4 w-4 mr-1.5" /> Plans
                {plans.length > 0 && <span className="ml-1.5 text-black/70">({plans.length})</span>}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141414] border-white/10 rounded-none text-white w-72 max-h-[26rem] overflow-y-auto">
              <DropdownMenuItem data-testid="action-new-plan" onClick={onNewPlan}
                className="text-white focus:bg-white/10 focus:text-white cursor-pointer gap-2 py-2.5">
                <FilePlus2 className="h-4 w-4 text-[#E2E30A]" /> <span className="text-sm">Start a new week/plan</span>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="action-duplicate" onClick={onDuplicate}
                className="text-white focus:bg-white/10 focus:text-white cursor-pointer gap-2 py-2.5">
                <CopyPlus className="h-4 w-4 text-[#E2E30A]" /> <span className="text-sm">Duplicate this week/plan</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-200">Load a saved plan</DropdownMenuLabel>
              {plans.length === 0 ? (
                <div className="px-2 py-3 text-xs text-zinc-200">No saved plans yet. Your plan saves automatically as you build it.</div>
              ) : (
                plans.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 px-2 py-2 hover:bg-white/5">
                    <button type="button" data-testid={`load-plan-${p.id}`} onClick={() => onLoadPlan(p.id)} className="flex-1 min-w-0 text-left">
                      <span className="text-sm text-white flex items-center gap-2 truncate">
                        {p.id === activePlanId && <Check className="h-3 w-3 text-[#E2E30A] shrink-0" />}
                        <span className="truncate">{p.name || "Untitled plan"}</span>
                      </span>
                      <span className="text-[10px] text-zinc-200 block">
                        Step {p.currentStep || 1}/8 · updated {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "—"}
                      </span>
                    </button>
                    <button type="button" data-testid={`rename-plan-${p.id}`} title="Rename"
                      onClick={(e) => { e.stopPropagation(); handleRename(p); }}
                      className="p-1.5 text-zinc-200 hover:text-[#E2E30A] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`delete-plan-${p.id}`} title="Delete"
                      onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                      className="p-1.5 text-zinc-200 hover:text-[#FF3B30] transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
      )} />

      {/* Row 3 — clickable step progress */}
      <div className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 overflow-x-auto">
          <div className="flex items-center gap-1 py-2 min-w-max">
            {(steps || STEPS).map((s, si, arr) => {
              const done = s.n < currentStep;
              const active = s.n === currentStep;
              const clickable = s.n <= maxStep;
              return (
                <div key={s.n} className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid={`step-indicator-${s.n}`}
                    disabled={!clickable}
                    onClick={() => clickable && onStepClick && onStepClick(s.n)}
                    className={`flex items-center gap-2 px-3 py-1.5 border transition-colors ${
                      active ? "border-[#004AAD] bg-[#004AAD]/15"
                      : done ? "border-[#E2E30A]/40 bg-transparent"
                      : "border-white/10 bg-transparent"
                    } ${clickable ? "cursor-pointer hover:border-[#E2E30A]/70 hover:bg-white/5" : "cursor-not-allowed opacity-50"}`}
                  >
                    <span className={`text-[10px] font-mono ${active || done ? "text-[#E2E30A]" : "text-zinc-200"}`}>
                      {done ? "✓" : String(s.n).padStart(2, "0")}
                    </span>
                    <span className={`text-[11px] uppercase tracking-wider ${active ? "text-white" : "text-zinc-200"}`}>{s.label}</span>
                  </button>
                  {si < arr.length - 1 && <div className="w-3 h-px bg-white/10" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      <SubscriptionDialog open={subOpen} onOpenChange={setSubOpen} />
      <AccountSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </header>
    </>
  );
}
